import { lazy, Suspense } from 'react';
import { Button } from '@ai-tutor/ui';

/**
 * The hero is lazy because `routes.tsx` imports the 404 page EAGERLY, as the
 * `*` catch-all. Anything it pulls in lands in the entry chunk every user
 * downloads on every page, and GSAP plus two of its plugins — for a page almost
 * nobody reaches — does not belong there. Same shape as `PDFViewer.tsx`, the
 * repo's other `lazy` boundary.
 */
const GridGlyph404 = lazy(() =>
  import('@/components/core/animations/GridGlyph404').then(m => ({
    default: m.GridGlyph404,
  }))
);

export interface NotFoundHeroProps {
  /** Decides where the single escape route goes, and what it is called. */
  signedIn: boolean;
  onHome: () => void;
}

/**
 * The body of the 404 screen: the lattice glyph, a line of copy, one way out.
 *
 * Extracted from `NotFoundPage`, where it was a module-local `NotFoundContent`
 * — a name that described its position in the file rather than what it draws,
 * and which could not be storied because nothing exported it. Its props were
 * already clean; only its address was wrong.
 *
 * `sr-only` h1, not a visible one. `404` alone is meaningless read aloud, so
 * the accessible name is a sentence and the glyph is `aria-hidden`. This is the
 * page's only heading.
 *
 * `fallback={null}` rather than a static stand-in: the glyph reserves its own
 * box only once measured, so a hand-written placeholder would have to restate
 * the layout maths — two copies of the one thing that must not drift. The
 * meaning is carried by the h1 and the tagline, both of which paint
 * immediately; the lattice is decoration that arrives a beat later.
 */
export function NotFoundHero({ signedIn, onHome }: NotFoundHeroProps) {
  return (
    <div className='text-center'>
      <h1 className='sr-only'>Page not found (404)</h1>

      <Suspense fallback={null}>
        <GridGlyph404 />
      </Suspense>

      {/* The glyph is 336px tall at `md` (and 112px on a phone), so the gap is in proportion to it —
          at `mt-8` the sentence read as a caption hanging off the bottom of the
          artwork rather than as its own line. */}
      <p className='mb-10 mt-8 text-xl text-muted-foreground sm:mt-14 md:mt-20 md:text-2xl'>
        This page is missing, but your next lesson isn&apos;t.
      </p>

      {/* Opaque at first paint, deliberately: an older version faded this in at
          t=6.4s of a 7s timeline, gating the only escape route from an error
          page behind the animation finishing. */}
      <Button onClick={onHome} size='lg' className='px-8 text-lg max-sm:h-11'>
        {signedIn ? 'Back to Home' : 'Go to Login'}
      </Button>
    </div>
  );
}
